import {animate, state, style, transition, trigger} from "@angular/animations";
import {SubRole, EffectiveRole} from "../_models/user";
import {CompanyStyle, CompanyType} from "../_models/company";
import {TaskDueDate, TaskPriority, TaskStatus, TaskType} from "../_models/task";
import {AreaStatus, AreaWeight} from "../_models/area";
import {OrderStatus, SubmittalStatus} from "../_models/material";
import {DocumentType} from "../_models/document";
import {MyTaskFilter} from "../components/admin/components/my-tasks/my-tasks.component";
import {LinkedElementType} from "../_models/linked-element";
import {ChangeDateHistoryType} from "../_models/entityWithTabs";
import {ElementsDueDate, ElementsStartDate} from "../_models/elements-filter";
import {CreatedByFilter} from "../_models/global-template";
import {TradeType} from "../_models/trade";
import {SelectorOption} from "../components/admin/components/inputs/status-selector/status-selector.component";
import {Project, ProjectUnitType} from "../_models/project";
import {getTaskTypeLabelMap} from "../_util/utils";

export const CHANGES_SAVED_MESSAGE = 'Changes have been saved';

export const modalAnimation = trigger('modalAnimation', [
  state('void', style({
    visibility: 'hidden',
    opacity: 0
  })),
  state('*', style({
    visibility: 'visible',
    opacity: 1
  })),
  transition('void <=> *', animate('0.3s ease-in-out')),
  transition('* => void', animate('0.3s ease-in-out'))
]);

export const emailPattern = "^[^\\s@]+@(?:[^\\s@.-](?:[^\\s@.]*[^\\s@.-])?\\.)+(?=.{2,}$)[^\\s@.-](?:[^\\s@.]*[^\\s@.-])?$";
export const phoneMask = "(000) 000-0000";
export const US_DIAL_CODE = "+1";
export const phoneMaskGeneric = "0000000000000";
export const phonePlaceholderGeneric = "Phone number";

// The US-style mask only applies to +1; other countries accept plain digits (up to 13).
export function getPhoneMask(dialCode: string | null | undefined): string {
  return dialCode === US_DIAL_CODE || !dialCode ? phoneMask : phoneMaskGeneric;
}

export function getPhonePlaceholder(dialCode: string | null | undefined): string {
  return dialCode === US_DIAL_CODE || !dialCode ? phoneMask : phonePlaceholderGeneric;
}

export enum SortType {ASC="ASC", DESC="DESC"}

export const companyTypeByEnum: Map<CompanyType, string> = new Map<CompanyType, string>([
  [CompanyType.CABINETS, "Cabinets"],
  [CompanyType.DEVELOPER, "Developer"],
  [CompanyType.ELECTRICAL, "Electrical"],
  [CompanyType.FLOORING, "Flooring"],
  [CompanyType.FRAMING, "Framing"],
  [CompanyType.GENERAL_CONTRACTOR, "General Contractor"],
  [CompanyType.HOTEL, "Hotel"],
  [CompanyType.MECHANICAL, "Mechanical"],
  [CompanyType.MILLWORK, "Millwork"],
  [CompanyType.HVAC, "HVAC"],
  [CompanyType.LOW_VOLTAGE, "Low Voltage"],
  [CompanyType.PAINTING, "Painting"],
  [CompanyType.PLUMBING, "Plumbing"],
  [CompanyType.TILING, "Tiling"]
]);

export const companyStyleByEnum: Map<CompanyStyle, string> = new Map<CompanyStyle, string>([
  [CompanyStyle.SOLO, "Solo"],
  [CompanyStyle.TRADES, "Trade"],
]);

export const createdByTemplatesByEnum: Map<CreatedByFilter, string> = new Map<CreatedByFilter, string>([
  [CreatedByFilter.ALL, "All templates"],
  [CreatedByFilter.ME, "Created by me"],
]);

export const tradesTypeByEnum: Map<TradeType, string> =
  new Map<TradeType, string>([
    [TradeType.CARPENTRY, 'Carpentry'],
    [TradeType.CEILINGS_ACT, 'Ceilings (ACT)'],
    [TradeType.CONCRETE, 'Concrete'],
    [TradeType.DOORS_AND_HARDWARE, 'Doors & Hardware'],
    [TradeType.DRYWALL, 'Drywall'],
    [TradeType.ELECTRICAL, 'Electrical'],
    [TradeType.ELEVATOR, 'Elevator'],
    [TradeType.FIRE_PROTECTION, 'Fire Protection'],
    [TradeType.FLOORING, 'Flooring'],
    [TradeType.GLASS_AND_GLAZING, 'Glass & Glazing'],
    [TradeType.HVAC_MECHANICAL, 'HVAC / Mechanical'],
    [TradeType.KITCHEN_EQUIPMENT, 'Kitchen Equipment'],
    [TradeType.LANDSCAPING, 'Landscaping'],
    [TradeType.LOW_VOLTAGE_FIRE_ALARM, 'Low Voltage / Fire Alarm'],
    [TradeType.MASONRY, 'Masonry'],
    [TradeType.MILLWORK_AND_CABINETS, 'Millwork / Cabinets'],
    [TradeType.PAINTING, 'Painting'],
    [TradeType.PLUMBING, 'Plumbing'],
    [TradeType.ROOFING, 'Roofing'],
    [TradeType.SECURITY_ACCESS_CONTROL, 'Security / Access Control'],
    [TradeType.SITEWORK_EXCAVATION, 'Sitework / Excavation'],
    [TradeType.STEEL, 'Steel'],
    [TradeType.TILE_AND_STONE, 'Tile / Stone'],
    [TradeType.WATERPROOFING, 'Waterproofing'],
    [TradeType.WINDOWS_EXTERIOR_FACADE, 'Windows / Exterior Facade'],
    [TradeType.FINAL_CLEAN_TURNOVER, 'Final Clean / Turnover'],
  ]);

export const taskPriorityByEnum: Map<TaskPriority, string> = new Map<TaskPriority, string>([
  [TaskPriority.NO_PRIORITY, "No priority"],
  [TaskPriority.LOW, "Low"],
  [TaskPriority.MEDIUM, "Medium"],
  [TaskPriority.HIGH, "High"],
]);

export const taskTypeByEnum: Map<TaskType, string> = new Map<TaskType, string>([
  [TaskType.PROJECT_TASK, "Project Tasks"],
  [TaskType.FLOOR_TASK, "Floor Task"],
  [TaskType.UNIT_TASK, "Unit Task"],
  [TaskType.ROOM_TASK, "Room Task"],
  [TaskType.AREA_TASK, "Area Task"],

  [TaskType.GENERAL_TASK, "General Task"],
]);

export const linkedElementTypeByEnum: Map<LinkedElementType, string> = new Map<LinkedElementType, string>([
  [LinkedElementType.PROJECT_ELEMENT, "Project"],
  [LinkedElementType.FLOOR_ELEMENT, "Floor"],
  [LinkedElementType.UNIT_ELEMENT, "Unit"],
  [LinkedElementType.ROOM_ELEMENT, "Room"],
  [LinkedElementType.AREA_ELEMENT, "Area"],
]);

export const linkedElementTypeWithNumberByEnum: Map<LinkedElementType, number> = new Map<LinkedElementType, number>([
  [LinkedElementType.FLOOR_ELEMENT, 0],
  [LinkedElementType.UNIT_ELEMENT, 1],
  [LinkedElementType.ROOM_ELEMENT, 2],
  [LinkedElementType.AREA_ELEMENT, 3],
]);

export const taskDueDateByEnum: Map<TaskDueDate, string> = new Map<TaskDueDate, string>([
  [TaskDueDate.PAST_DUE, "Past Due"],
  [TaskDueDate.DUE_TODAY, "Due Today"],
  [TaskDueDate.DUE_THIS_WEEK, "Due this week"],
  [TaskDueDate.DUE_THIS_MONTH, "Due this month"],
]);

export const taskStatusByEnum: Map<TaskStatus, string> = new Map<TaskStatus, string>([
  [TaskStatus.TO_DO, "To Do"],
  [TaskStatus.IN_PROGRESS, "In progress"],
  [TaskStatus.READY, "Ready"],
  [TaskStatus.DONE, "Done"],
]);

export const TASK_STATUS_OPTIONS: SelectorOption<TaskStatus>[] = [
  { value: TaskStatus.TO_DO, label: 'To Do', styleKey: 'task.todo' },
  { value: TaskStatus.IN_PROGRESS, label: 'In progress', styleKey: 'task.progress' },
  { value: TaskStatus.READY, label: 'Ready', styleKey: 'task.ready' },
  { value: TaskStatus.DONE, label: 'Done', styleKey: 'task.done' }
];

export const TASK_TYPE_OPTIONS: SelectorOption<TaskType>[] = [
  { value: TaskType.GENERAL_TASK, label: 'General Task', styleKey: 'other', iconName: 'general-task-type-full' },
  { value: TaskType.PROJECT_TASK, label: 'Project Task', styleKey: 'other', iconName: 'project-task-type-full' },
  { value: TaskType.FLOOR_TASK, label: 'Floor Task', styleKey: 'other', iconName: 'floor-task-type-full' },
  { value: TaskType.UNIT_TASK, label: 'Unit Task', styleKey: 'other', iconName: 'unit-task-type-full' },
  { value: TaskType.ROOM_TASK, label: 'Room Task', styleKey: 'other', iconName: 'room-task-type-full' },
  { value: TaskType.AREA_TASK, label: 'Area Task', styleKey: 'other', iconName: 'area-task-type-full' }
];

export function getAvailableTaskTypeOptions(project: Project | null, nonProjectOptionId = 0): SelectorOption<TaskType>[] {
  if (!project) {
    return [];
  }

  if (project.id === nonProjectOptionId) {
    return TASK_TYPE_OPTIONS.filter(option => option.value === TaskType.GENERAL_TASK);
  }

  const labelMap = getTaskTypeLabelMap(project);

  return TASK_TYPE_OPTIONS
    .filter(option => {
      if (option.value === TaskType.GENERAL_TASK) {
        return false;
      }

      if (option.value === TaskType.UNIT_TASK) {
        return project.unitType !== ProjectUnitType.SINGLE;
      }

      return true;
    })
    .map(option => ({
      ...option,
      label: labelMap.get(option.value) || option.label
    }));
}

export const myTaskFiltersByEnum: Map<MyTaskFilter, string> = new Map<MyTaskFilter, string>([
  [MyTaskFilter.ALL_TASKS, "All Tasks"],
  [MyTaskFilter.ASSIGNED_TO_ME, "Assigned to me"],
  [MyTaskFilter.CREATED_BY_ME, "Created by me"],
]);

export const areaStatusByEnum: Map<AreaStatus, string> = new Map<AreaStatus, string>([
  [AreaStatus.TO_DO, "To Do"],
  [AreaStatus.IN_PROGRESS, "In Progress"],
  [AreaStatus.DONE, "Done"],
]);

export const areaWeightByEnum: Map<AreaWeight, string> = new Map<AreaWeight, string>([
  [AreaWeight.NO_WEIGHT, 'No Weight'],
  [AreaWeight.SMALL, "Small"],
  [AreaWeight.MEDIUM, "Medium"],
  [AreaWeight.LARGE, "Large"],
  [AreaWeight.HUGE, "Huge"]
]);

export const effectiveRoleDescByEnum: Map<EffectiveRole, string> = new Map<EffectiveRole, string>([
  [EffectiveRole.ROLE_SUPER_ADMIN, "Super Admin"],
  [EffectiveRole.ROLE_COMPANY_ADMIN, "Admin"],
  [EffectiveRole.ROLE_USER, "User"],
  [EffectiveRole.ROLE_TRADE, "Trade"],
]);

export const subRoleDescByEnum: Map<SubRole, string> = new Map<SubRole, string>([
  [SubRole.ADMIN, "Admin"],
  [SubRole.MANAGER, "Manager"],
  [SubRole.MEMBER, "Member"],
  [SubRole.VIEWER, "Viewer"],
  [SubRole.TRADE, "Trade"]
]);

export const SUBROLE_OPTIONS: SelectorOption<SubRole>[] = [
  { value: SubRole.ADMIN, label: 'Admin', styleKey: 'subrole.admin' },
  { value: SubRole.MANAGER, label: 'Manager', styleKey: 'subrole.manager' },
  { value: SubRole.MEMBER, label: 'Member', styleKey: 'subrole.member' },
  { value: SubRole.VIEWER, label: 'Viewer', styleKey: 'subrole.viewer' },
  { value: SubRole.TRADE, label: 'Trade', styleKey: 'subrole.trade' }
];

export const EFFECTIVE_ROLE_OPTIONS: SelectorOption<EffectiveRole>[] = [
  { value: EffectiveRole.ROLE_COMPANY_ADMIN, label: 'Admin', styleKey: 'subrole.admin' },
  { value: EffectiveRole.ROLE_USER, label: 'User', styleKey: 'subrole.member' },
  { value: EffectiveRole.ROLE_TRADE, label: 'Trade', styleKey: 'subrole.trade' }
];

export const effectiveRoleDescByEnumForTable: Map<EffectiveRole, string> = new Map<EffectiveRole, string>([
  [EffectiveRole.ROLE_SUPER_ADMIN, "Super Admin"],
  [EffectiveRole.ROLE_COMPANY_ADMIN, "Admin"],
  [EffectiveRole.ROLE_USER, "User"],
  [EffectiveRole.ROLE_TRADE, "Trade"]
]);

export const subRoleDescByEnumForTable: Map<SubRole, string> = new Map<SubRole, string>([
  [SubRole.ADMIN, "Admin"],
  [SubRole.MANAGER, "Manager"],
  [SubRole.MEMBER, "Member"],
  [SubRole.VIEWER, "Viewer"],
  [SubRole.TRADE, "Trade"]
]);

export let userEffectiveRoleStringForSelector: Map<EffectiveRole, string> = new Map<EffectiveRole, string>([
  [EffectiveRole.ROLE_COMPANY_ADMIN, "Admin"],
  [EffectiveRole.ROLE_USER, "User"],
  [EffectiveRole.ROLE_TRADE, "Trade"],
]);

export let userSubRoleStringForSelector: Map<SubRole, string> = new Map<SubRole, string>([
  [SubRole.ADMIN, "Admin"],
  [SubRole.MANAGER, "Manager"],
  [SubRole.MEMBER, "Member"],
  [SubRole.VIEWER, "Viewer"],
  [SubRole.TRADE, "Trade"],
]);

export let submittalStatusStringForSelector: Map<SubmittalStatus, string> = new Map<SubmittalStatus, string>([
  [SubmittalStatus.NOT_SUBMITTED, "Not Submitted"],
  [SubmittalStatus.SAMPLES_ORDERED, "Samples Ordered"],
  [SubmittalStatus.SUBMITTED, "Submitted"],
  [SubmittalStatus.REJECTED, "Rejected"],
  [SubmittalStatus.APPROVED_AS_NOTED, "Approved As Noted"],
  [SubmittalStatus.APPROVED, "Approved"],
]);

export let SUBMITTAL_STATUS_OPTIONS: SelectorOption<SubmittalStatus>[] = [
  {value: SubmittalStatus.NOT_SUBMITTED, label: 'Not Submitted', styleKey: 'submittal.not-submitted'},
  {value: SubmittalStatus.SAMPLES_ORDERED, label: 'Samples Ordered', styleKey: 'submittal.samples-ordered'},
  {value: SubmittalStatus.SUBMITTED, label: 'Submitted', styleKey: 'submittal.submitted'},
  {value: SubmittalStatus.REJECTED, label: 'Rejected', styleKey: 'submittal.rejected'},
  {value: SubmittalStatus.APPROVED_AS_NOTED, label: 'Approved As Noted', styleKey: 'submittal.approved-as-noted'},
  {value: SubmittalStatus.APPROVED, label: 'Approved', styleKey: 'submittal.approved'}
];

export let ORDER_STATUS_OPTIONS: SelectorOption<OrderStatus>[] = [
  {value: OrderStatus.NOT_ORDERED, label: 'Not Ordered', styleKey: 'order.not-ordered'},
  {value: OrderStatus.PO_SUBMITTED, label: 'PO Submitted', styleKey: 'order.po-submitted'},
  {value: OrderStatus.PARTIALLY_ORDERED, label: 'Partially Ordered', styleKey: 'order.partially-ordered'},
  {value: OrderStatus.ORDERED, label: 'Ordered', styleKey: 'order.ordered'},
  {value: OrderStatus.PARTIALLY_ARRIVED, label: 'Partially Arrived', styleKey: 'order.partially-arrived'},
  {value: OrderStatus.ARRIVED, label: 'Arrived', styleKey: 'order.arrived'}
];

export let orderStatusStringForSelector: Map<OrderStatus, string> = new Map<OrderStatus, string>([
  [OrderStatus.NOT_ORDERED, "Not Ordered"],
  [OrderStatus.PO_SUBMITTED, "PO Submitted"],
  [OrderStatus.PARTIALLY_ORDERED, "Partially Ordered"],
  [OrderStatus.ORDERED, "Ordered"],
  [OrderStatus.PARTIALLY_ARRIVED, "Partially Arrived"],
  [OrderStatus.ARRIVED, "Arrived"],
]);

export let documentTypeStringForSelector: Map<DocumentType, string> = new Map<DocumentType, string>([
  [DocumentType.FILE, "File"],
  [DocumentType.LINK, "Link"]
]);

//Elements date
export const startDateOptionsByEnum: Map<ElementsStartDate, string> = new Map<ElementsStartDate, string>([
  [ElementsStartDate.START_DELAYED, "Start delayed"],
  [ElementsStartDate.STARTS_TODAY, "Starts today"],
  [ElementsStartDate.STARTS_THIS_WEEK, "Starts this week"],
  [ElementsStartDate.STARTS_THIS_MONTH, "Starts this month"],
]);

export const dueDateOptionsByEnum: Map<ElementsDueDate, string> = new Map<ElementsDueDate, string>([
  [ElementsDueDate.PAST_DUE, "Past Due"],
  [ElementsDueDate.DUE_TODAY, "Due today"],
  [ElementsDueDate.DUE_THIS_WEEK, "Due this week"],
  [ElementsDueDate.DUE_THIS_MONTH, "Due this month"],
]);
